import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import LinkButton from 'assets/components/button/LinkButton';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';
import { defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import React from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import { ContractRefundPaymentAction, useContractRefundPaymentProvider } from '../Finance.ContractRefundPayment.Hooks';
import ContractRefundPaymentActions from './Finance.ContractRefundPayment.Actions';
import ContractRefundPaymentFilters from './Finance.ContractRefundPayment.Filters';
import ContractRefundPaymentInfo from './Finance.ContractRefundPayment.Info';

export default function ContractRefundPaymentList() {
  const { lang, translate } = getLocales();
  const { listApi, pageRouter, permissions, generateRemittance, isGeneratingRemittance } =
    useContractRefundPaymentProvider();

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-clipboard-list" />}
      header={lang.contractsPaymentHistory}
      render={lang.noInfo}
    />
  ) : (
    <Page
      class={ThemePageList}
      sidebar={<ContractRefundPaymentInfo />}
      isLoading={listApi.isExecuting}
      header={
        <>
          <h2 style={{ marginRight: 'auto' }} children={lang.contractsPaymentHistory} />
          <Box class={ThemeWrapperStack}>
            {isMobile && <ContractRefundPaymentFilters />}
            <ContractRefundPaymentActions key="actions" />
          </Box>
        </>
      }
      subHeader={isDesktop && <ContractRefundPaymentFilters />}
      render={
        <table key="list" className={ThemeTableSimple}>
          <thead>
            <tr>
              <th>{lang.referenceNumber}</th>
              <th>{lang.contractRefund}</th>
              <th>{lang.paymentMethod}</th>
              <th>{lang.payeeName}</th>
              <th>{lang.paymentAt}</th>
              <th>{lang.paidAmount}</th>
              <th>{lang.gstHstPaid}</th>
              <th>{lang.pstPaid}</th>
              <th>{lang.totalPaid}</th>
              <th data-actions>{lang.actions}</th>
            </tr>
          </thead>
          <tbody>
            <DataMap
              data={listApi.payload?.data}
              render={({ data: contractRefundPayment }) => {
                const isCancelled = contractRefundPayment?.isCancelled;
                const isCancellationDisabled = !permissions?.CANCEL_CONTRACTREFUNDPAYMENT || isCancelled;
                const jobId = contractRefundPayment.contractRefundPaymentItems?.[0]?.jobId;

                return (
                  <React.Fragment key={contractRefundPayment.id}>
                    <tr style={{ borderTop: `2px solid ${Theme.Color.shadow}` }}>
                      <td>
                        <b>{contractRefundPayment.referenceNumber}</b>
                      </td>
                      <td>{jobId ?? 'N/A'}</td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: Theme.Size.XS }}>
                          <span>
                            {isCancelled && (
                              <Box
                                class={ThemeTagRounded}
                                style={{ color: Theme.Color.error, backgroundColor: Theme.Color.errorLight }}
                              >
                                <Icon class="fas-check" />
                                &nbsp; {lang.cancelled}
                              </Box>
                            )}
                          </span>
                          <span>{translate(contractRefundPayment.paymentMethodCode)}</span>
                        </div>
                      </td>
                      <td>{contractRefundPayment.contractRefundPaymentPayee?.displayInfo?.title}</td>
                      <td>
                        {contractRefundPayment.owner?.fullName && (
                          <>
                            {contractRefundPayment.owner.fullName} {lang.at}
                            <br />
                          </>
                        )}
                        {contractRefundPayment.createdAtLocal
                          ? localizeDate(contractRefundPayment?.createdAtLocal, defaultDateTimeFormat)
                          : 'N/A'}
                      </td>
                      <td>
                        <b>
                          <FormatValue value={`${(contractRefundPayment.amountPaid || 0).toFixed(2)}$`} />
                        </b>
                      </td>
                      <td>
                        <b>
                          <FormatValue value={`${(contractRefundPayment.gstHstAmountPaid || 0).toFixed(2)}$`} />
                        </b>
                      </td>
                      <td>
                        <b>
                          <FormatValue
                            value={`${(contractRefundPayment.provincialSalesTaxAmountPaid || 0).toFixed(2)}$`}
                          />
                        </b>
                      </td>
                      <td>
                        <b>
                          <FormatValue value={`${(contractRefundPayment.totalPaidAmt || 0).toFixed(2)}$`} />
                        </b>
                      </td>
                      <td>
                        <Box class={ThemeWrapperStack}>
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="fas-info-circle" />}
                            onClick={pageRouter.updateParams}
                            data={{
                              id: contractRefundPayment.id.toString(),
                              action: ContractRefundPaymentAction.INFO,
                            }}
                            htmlElementProps={{ title: lang.info }}
                            active
                          />
                          <LinkButton
                            class={ThemeButtonCircle}
                            url={contractRefundPayment.remittanceDocument?.url}
                            media={<Icon class="fas-download" />}
                            target="_blank"
                            disabled={!contractRefundPayment?.remittanceDocument?.url}
                            htmlElementProps={{
                              title: !contractRefundPayment?.remittanceDocument?.url ? lang.missing : lang.download,
                            }}
                            active
                          />
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="if-ui-close" />}
                            onClick={pageRouter.updateParams}
                            data={{
                              id: contractRefundPayment.id.toString(),
                              action: ContractRefundPaymentAction.CANCEL,
                            }}
                            htmlElementProps={{ title: lang.cancelPayment }}
                            disabled={isCancellationDisabled}
                            active
                          />
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="fas-envelope" />}
                            onClick={pageRouter.updateParams}
                            data={{
                              id: contractRefundPayment.id.toString(),
                              action: ContractRefundPaymentAction.SEND_EMAIL,
                            }}
                            htmlElementProps={{ title: lang.sendEmail }}
                            active
                          />
                          <Button
                            class={ThemeButtonCircle}
                            media={<Icon class="fas-sync-alt" />}
                            onClick={() => generateRemittance(contractRefundPayment.id)}
                            htmlElementProps={{ title: lang.regenerateRemittance }}
                            disabled={isGeneratingRemittance(contractRefundPayment.id)}
                            active
                          />
                        </Box>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              }}
            />
          </tbody>
        </table>
      }
      footer={
        <Pagination
          class={ThemePaginationRounded}
          count={listApi.payload?.count}
          offset={listApi.body?.offset}
          limit={listApi.body?.limit}
          onLimitChange={(limit) => listApi.execute((body) => ({ ...body, limit, offset: 0 }))}
          onOffsetChange={(offset) => listApi.execute((body) => ({ ...body, offset }))}
        />
      }
    />
  );
}
